import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";

import {
  buildGatewayAdminRecordUrl,
  findGatewayBeefProductsByNames,
  searchGatewayBeefProducts,
} from "../../../services/missingReports";

const SEARCH_DEBOUNCE_MS = 250;

const BeefProductPicker = ({ selectedId, onSelectedIdChange, seedTerms = [] }) => {
  const [matchOptions, setMatchOptions] = useState([]);
  const [searchOptions, setSearchOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [forceSearch, setForceSearch] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");

  const seedTermA = seedTerms?.[0] || "";
  const seedTermB = seedTerms?.[1] || "";
  const seedTermC = seedTerms?.[2] || "";

  const cleanedSeedTerms = useMemo(
    () =>
      [seedTermA, seedTermB, seedTermC]
        .map((value) => (value || "").trim())
        .filter(Boolean)
        .slice(0, 3),
    [seedTermA, seedTermB, seedTermC]
  );
  const cleanedSeedTermsKey = cleanedSeedTerms.join("||");

  const hasInitialMatches = matchOptions.length > 0;
  const showSearch = forceSearch || !hasInitialMatches;
  const searchLoading = loadingMatches || loadingSearch;

  useEffect(() => {
    let active = true;
    async function loadInitial() {
      setInputValue("");
      setSelectedLabel("");
      setForceSearch(false);
      setSearchOptions([]);
      if (!cleanedSeedTerms.length) {
        setMatchOptions([]);
        return;
      }
      setLoadingMatches(true);
      try {
        const nextOptions = await findGatewayBeefProductsByNames(
          cleanedSeedTerms[0] || "",
          cleanedSeedTerms[1] || ""
        );
        if (!active) {
          return;
        }
        setMatchOptions(nextOptions);
      } catch (error) {
        if (!active) {
          return;
        }
        setMatchOptions([]);
      } finally {
        if (active) {
          setLoadingMatches(false);
        }
      }
    }
    loadInitial();
    return () => {
      active = false;
    };
  }, [cleanedSeedTermsKey, onSelectedIdChange]);

  useEffect(() => {
    if (selectedId || !matchOptions.length) {
      return;
    }

    const fallbackOption = matchOptions[0];
    if (!fallbackOption?.id) {
      return;
    }

    onSelectedIdChange(fallbackOption.id);
    setSelectedLabel(fallbackOption.label || "");
  }, [matchOptions, selectedId, onSelectedIdChange]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedLabel("");
      return;
    }

    const matched = [...matchOptions, ...searchOptions].find((option) => option.id === selectedId);
    if (matched?.label) {
      setSelectedLabel(matched.label);
    }
  }, [matchOptions, searchOptions, selectedId]);

  useEffect(() => {
    if (!showSearch) {
      return undefined;
    }

    let active = true;
    const query = inputValue.trim();
    if (!query) {
      setSearchOptions([]);
      return undefined;
    }

    const timer = setTimeout(() => {
      async function runSearch() {
        setLoadingSearch(true);
        try {
          const results = await searchGatewayBeefProducts(
            query,
            cleanedSeedTerms[1] || cleanedSeedTerms[0] || ""
          );
          if (!active) {
            return;
          }
          setSearchOptions(results);
        } catch (error) {
          if (!active) {
            return;
          }
          setSearchOptions([]);
        } finally {
          if (active) {
            setLoadingSearch(false);
          }
        }
      }
      runSearch();
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [inputValue, showSearch]);

  const valueOption = useMemo(() => {
    const matched = [...matchOptions, ...searchOptions].find((option) => option.id === selectedId);
    if (matched) {
      return matched;
    }
    if (!selectedId) {
      return null;
    }
    return {
      id: selectedId,
      label: selectedLabel || "Selected product",
    };
  }, [matchOptions, searchOptions, selectedId, selectedLabel]);

  return (
    <Stack spacing={1}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          {showSearch ? (
            <Autocomplete
              openOnFocus
              clearOnBlur={false}
              options={searchOptions}
              value={valueOption}
              inputValue={inputValue}
              filterOptions={(x) => x}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option?.label || ""}
              onChange={(_, option) => {
                onSelectedIdChange(option?.id || "");
                const label = option?.label || "";
                setInputValue(label);
                setSelectedLabel(label);
              }}
              onInputChange={(_, nextValue, reason) => {
                if (reason !== "input" && reason !== "clear") {
                  return;
                }
                setInputValue(nextValue);
                if (reason === "clear") {
                  onSelectedIdChange("");
                  setSelectedLabel("");
                }
              }}
              loading={searchLoading}
              noOptionsText="No matching products found."
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Search beef products"
                  helperText="Searches Gateway beef products."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel id="beef-product-match-select-label">Select beef product</InputLabel>
              <Select
                labelId="beef-product-match-select-label"
                label="Select beef product"
                value={selectedId || ""}
                onChange={(event) => {
                  const nextId = event.target.value;
                  const selectedOption = matchOptions.find((option) => option.id === nextId);
                  onSelectedIdChange(nextId);
                  setSelectedLabel(selectedOption?.label || "");
                }}
              >
                {matchOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            if (showSearch && hasInitialMatches) {
              setForceSearch(false);
              return;
            }
            setForceSearch(true);
            if (!inputValue && cleanedSeedTerms.length) {
              setInputValue(cleanedSeedTerms.join(" ").trim());
            }
          }}
          disabled={showSearch && !hasInitialMatches}
          sx={{ mt: 0.5, whiteSpace: "nowrap", minWidth: 110 }}
        >
          {showSearch ? "Show matches" : "Full search"}
        </Button>
      </Box>
      {selectedId ? (
        <Link
          href={buildGatewayAdminRecordUrl("beef_product", selectedId)}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          Open beef product in Gateway
        </Link>
      ) : null}
    </Stack>
  );
};

export default BeefProductPicker;
